// src/pages/vendas/ModalEditarItensNota.js
import React, { useEffect, useState } from 'react';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalTable from '../../components/modalForm/ModalTable';
import Input from '../../components/input/Input';
import { ModalActionButton } from '../../components/modalForm/ModalActions';
import './ModalEditarItensNota.css';
import {
    formatCurrency,
    formatNumeric,
    normalizeDecimalInputBr,
    normalizeMoney,
    parseNumeric,
    sumMoney,
    toMoneyCents
} from '../../utils/numberUtils';
import { FaCheckCircle, FaExclamationTriangle, FaBoxOpen, FaSearch, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { useProduto } from '../../contexts/produtos/ProdutoContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { NotaItem } from '../../models/vendas/NotaItem';

// Monta o estado editável de cada item a partir do NotaItem original, mantendo
// os campos numéricos já formatados no padrão pt-BR usado pelos inputs monetários.
const montarItemEditavel = (item, index) => ({
    ...item,
    _key: item.id || item.comandaItemId || `novo-${index}`,
    xProdEdit: item.xProd || '',
    qtdComEdit: formatNumeric(item.qtdCom, 3),
    vUnComEdit: formatNumeric(item.vUnCom, 2)
});

// Recalcula o valor total do item (vProd) a partir da quantidade e do valor
// unitário informados, seguindo a mesma regra de arredondamento em centavos
// usada no restante do PDV (ver services/vendas/PDVService.js).
const recalcularVProdItem = (item) => normalizeMoney(
    parseNumeric(item.qtdComEdit) * parseNumeric(item.vUnComEdit)
);

// Modal para edição dos itens (NotaItem) que vão compor a nota fiscal antes da
// finalização da venda. Permite ajustar descrição, quantidade e valor unitário
// de cada item, sempre validando que a soma dos itens não fique divergente do
// total de produtos já calculado para a nota (nota.vProd).
const ModalEditarItensNota = ({ show, nota, onSalvar, onClose }) => {
    const [itens, setItens] = useState([]);
    const { showProdutoSearch } = useProduto();
    const { showYesNo } = useYesNo();

    useEffect(() => {
        if (show) {
            const notaItens = Array.isArray(nota?.notaItens) ? nota.notaItens : [];
            setItens(notaItens.map(montarItemEditavel));
        }
    }, [show, nota]);

    const handleFocusSelectAll = (e) => e.target.select();

    const handleChangeDescricao = (index, value) => {
        setItens((prev) => prev.map((item, i) => (i === index ? { ...item, xProdEdit: value } : item)));
    };

    const handleChangeQtde = (index, value) => {
        const valorNormalizado = normalizeDecimalInputBr(value, 3);
        setItens((prev) => prev.map((item, i) => (i === index ? { ...item, qtdComEdit: valorNormalizado } : item)));
    };

    const handleBlurQtde = (index) => {
        setItens((prev) => prev.map((item, i) => (i === index
            ? { ...item, qtdComEdit: formatNumeric(parseNumeric(item.qtdComEdit), 3) }
            : item)));
    };

    const handleChangeValorUnitario = (index, value) => {
        const valorNormalizado = normalizeDecimalInputBr(value, 2);
        setItens((prev) => prev.map((item, i) => (i === index ? { ...item, vUnComEdit: valorNormalizado } : item)));
    };

    const handleBlurValorUnitario = (index) => {
        setItens((prev) => prev.map((item, i) => (i === index
            ? { ...item, vUnComEdit: formatNumeric(parseNumeric(item.vUnComEdit), 2) }
            : item)));
    };

    // Abre a busca de produtos (ProdutoSearchDelivery no desktop, ProdutoSearch
    // no mobile) e troca o produto do item, atualizando descrição, CFOP, unidade
    // e valor unitário sugerido. Quantidade e edições manuais de preço são
    // preservadas apenas quando fizer sentido: o preço é atualizado para o do
    // novo produto, mas a quantidade informada pelo usuário é mantida.
    const handleTrocarProduto = async (index) => {
        const produtoSelecionado = await showProdutoSearch();

        if (!produtoSelecionado?.id) {
            return;
        }

        setItens((prev) => prev.map((item, i) => (i === index
            ? {
                ...item,
                produtoId: produtoSelecionado.id,
                produto: produtoSelecionado,
                xProdEdit: produtoSelecionado.descricao || item.xProdEdit,
                cfop: produtoSelecionado.cfop || item.cfop,
                unCom: produtoSelecionado.unTrib || produtoSelecionado.unMedida?.sigla || item.unCom,
                vUnComEdit: formatNumeric(produtoSelecionado.preco, 2)
            }
            : item)));
    };

    // Abre a busca de produtos e inclui um novo item na nota, já com quantidade
    // 1 e o valor unitário sugerido pelo cadastro do produto selecionado.
    const handleAdicionarItem = async () => {
        const produtoSelecionado = await showProdutoSearch();

        if (!produtoSelecionado?.id) {
            return;
        }

        setItens((prev) => {
            const precoUnitario = parseNumeric(produtoSelecionado.preco);
            const novoNotaItem = new NotaItem();
            novoNotaItem.produtoId = produtoSelecionado.id;
            novoNotaItem.produto = produtoSelecionado;
            novoNotaItem.item = prev.length + 1;
            novoNotaItem.cfop = produtoSelecionado.cfop || '';
            novoNotaItem.unCom = produtoSelecionado.unTrib || produtoSelecionado.unMedida?.sigla || '';
            novoNotaItem.qtdCom = 1;
            novoNotaItem.vUnCom = precoUnitario;
            novoNotaItem.vProd = normalizeMoney(precoUnitario);
            novoNotaItem.xProd = produtoSelecionado.descricao || '';

            return [...prev, montarItemEditavel(novoNotaItem, prev.length)];
        });
    };

    const handleRemoverItem = async (index) => {
        const confirmado = await showYesNo('Deseja remover este item da nota?');

        if (!confirmado) {
            return;
        }

        setItens((prev) => prev
            .filter((_, i) => i !== index)
            .map((item, i) => ({ ...item, item: i + 1 })));
    };

    const totalItens = sumMoney(itens.map(recalcularVProdItem));
    const totalNota = normalizeMoney(nota?.vProd);
    const diferencaCentavos = toMoneyCents(totalItens) - toMoneyCents(totalNota);
    const possuiDivergencia = diferencaCentavos !== 0;
    const possuiItemInvalido = itens.some((item) => parseNumeric(item.qtdComEdit) <= 0);
    const possuiDescricaoVazia = itens.some((item) => !item.xProdEdit?.trim());
    const notaSemItens = itens.length === 0;
    const bloqueiaSalvar = possuiDivergencia || possuiItemInvalido || possuiDescricaoVazia || notaSemItens;

    const handleSalvar = () => {
        if (bloqueiaSalvar) {
            return;
        }

        const notaItensAtualizados = itens.map((item) => {
            // eslint-disable-next-line no-unused-vars
            const { _key, xProdEdit, qtdComEdit, vUnComEdit, ...notaItemOriginal } = item;

            return {
                ...notaItemOriginal,
                xProd: xProdEdit.trim(),
                qtdCom: parseNumeric(qtdComEdit),
                vUnCom: parseNumeric(vUnComEdit),
                vProd: recalcularVProdItem(item)
            };
        });

        onSalvar(notaItensAtualizados);
    };

    if (!show) {
        return null;
    }

    return (
        <ModalContainer largura="860px" show={show} variant="cadastro" onHide={onClose} contentClassName="editar-itens-modal">
            <ModalHeader title="Editar Itens da Nota" onHide={onClose} />

            <ModalBody>
                <div className="editar-itens-toolbar">
                    <button
                        type="button"
                        className="btn btn-primary modal-action"
                        onClick={handleAdicionarItem}
                    >
                        <FaPlus />
                        Adicionar Item
                    </button>
                </div>

                <ModalTable className="editar-itens-table" containerStyle={{ maxHeight: '360px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>Item</th>
                            <th>Produto</th>
                            <th style={{ width: '100px' }}>Qtde</th>
                            <th style={{ width: '120px' }}>Vlr. Unitário</th>
                            <th style={{ width: '120px' }}>Vlr. Total</th>
                            <th style={{ width: '70px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {itens.map((item, index) => (
                            <tr key={item._key}>
                                <td style={{ textAlign: 'center' }}>{item.item || index + 1}</td>
                                <td style={{ whiteSpace: 'normal' }}>
                                    <Input label={`Descrição do item ${item.item || index + 1}`} labelHidden size="100%" type="text"
                                        value={item.xProdEdit}
                                        onChange={(e) => handleChangeDescricao(index, e.target.value)}
                                        onFocus={handleFocusSelectAll}
                                    />
                                </td>
                                <td>
                                    <Input label={`Quantidade do item ${item.item || index + 1}`} labelHidden size="100%" type="text"
                                        style={{ textAlign: 'right' }}
                                        value={item.qtdComEdit}
                                        onChange={(e) => handleChangeQtde(index, e.target.value)}
                                        onFocus={handleFocusSelectAll}
                                        onBlur={() => handleBlurQtde(index)}
                                        inputMode="decimal"
                                    />
                                </td>
                                <td>
                                    <Input label={`Valor unitário do item ${item.item || index + 1}`} labelHidden size="100%" type="text"
                                        style={{ textAlign: 'right' }}
                                        value={item.vUnComEdit}
                                        onChange={(e) => handleChangeValorUnitario(index, e.target.value)}
                                        onFocus={handleFocusSelectAll}
                                        onBlur={() => handleBlurValorUnitario(index)}
                                        inputMode="decimal"
                                    />
                                </td>
                                <td className="item-nota-total-linha">
                                    {formatCurrency(recalcularVProdItem(item))}
                                </td>
                                <td>
                                    <div className="item-nota-acoes">
                                        <button
                                            type="button"
                                            className="item-nota-btn-acao trocar"
                                            onClick={() => handleTrocarProduto(index)}
                                            title="Escolher outro produto"
                                            aria-label="Escolher outro produto"
                                        >
                                            <FaSearch />
                                        </button>
                                        <button
                                            type="button"
                                            className="item-nota-btn-acao remover"
                                            onClick={() => handleRemoverItem(index)}
                                            title="Remover item"
                                            aria-label="Remover item"
                                        >
                                            <FaTrashAlt />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {itens.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'center', padding: '16px' }}>
                                    <FaBoxOpen style={{ marginRight: '6px' }} />
                                    Nenhum item para editar.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </ModalTable>

                <div className={`editar-itens-resumo ${possuiDivergencia ? 'divergente' : 'ok'}`}>
                    <div className="editar-itens-resumo-totais">
                        <div className="editar-itens-resumo-linha">
                            <span>Total dos Itens:</span>
                            <b>{formatCurrency(totalItens)}</b>
                        </div>
                        <div className="editar-itens-resumo-linha">
                            <span>Total da Nota:</span>
                            <b>{formatCurrency(totalNota)}</b>
                        </div>
                    </div>
                    <div className="editar-itens-resumo-status">
                        {possuiDivergencia ? (
                            <>
                                <FaExclamationTriangle />
                                <span>
                                    Diferença de {formatCurrency(Math.abs(diferencaCentavos) / 100)}: ajuste os itens
                                    até que o total bata com o total da nota.
                                </span>
                            </>
                        ) : (
                            <>
                                <FaCheckCircle />
                                <span>Total dos itens confere com o total da nota.</span>
                            </>
                        )}
                    </div>
                </div>
            </ModalBody>

            <ModalFooter>
                <ModalActionButton action="close" onClick={onClose}>
                    Cancelar
                </ModalActionButton>
                <ModalActionButton action="save"
                    onClick={handleSalvar}
                    disabled={bloqueiaSalvar}
                    title={possuiDivergencia
                        ? 'O total dos itens precisa bater com o total da nota.'
                        : notaSemItens
                            ? 'A nota precisa ter ao menos um item.'
                            : undefined}
                >
                    Salvar Itens
                </ModalActionButton>
            </ModalFooter>
        </ModalContainer>
    );
};

export default ModalEditarItensNota;
